import React from 'react';
type Tome = {
  number: string;
  title: string;
  weeks: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Start the Stack',
  weeks: 'Weeks 1–6',
  framework: 'The Stacked Session Method · The 4-Row Stack',
  promise: 'Set up your groups and run your first stacked sessions: labeling, simple sentences and turn-taking.',
  badge: 'bg-coral',
  contents: ['Themes: The Lost Backpack, The Playground, The Lunchbox, The Bus Ride, The Class Pet, The Library', 'Place any student on a row and level in under a minute', 'WS-01 Caseload Stack Map: sort every group into rows and levels', '10 no-prep warm-ups that work for every row', 'Groups of 5 or more: a rotation plan that keeps everyone talking', 'The 5-minute baseline probe to place each student']
}, {
  number: '02',
  title: 'Describe and Sort',
  weeks: 'Weeks 7–12',
  framework: 'The Describe Ladder · Sort-Say-Show',
  promise: 'From naming an object to a full description — plus categories and asking for help.',
  badge: 'bg-sage',
  contents: ['Themes: The Supermarket, The Bakery, The Toy Shop, The Farm, The Art Room, The Garden', 'Vague vs. rich describing: the cues that close the gap', 'A category routine you can run with any theme', 'WS-10 Category Sort Mat, reusable with every theme', 'Describing warm-ups with any object in the room', 'The 5-minute describing probe']
}, {
  number: '03',
  title: 'Ask and Follow',
  weeks: 'Weeks 13–18',
  framework: 'The Question Staircase · The Direction Builder',
  promise: 'WH-questions from what and who to why and how, and directions built one step at a time.',
  badge: 'bg-smoke',
  contents: ['Themes: The Vet Clinic, The Fire Station, The Post Office, The Airport, The Train Station, The Building Site', 'Directions that confuse vs. directions that build skills', 'WS-18 Direction Card Maker: 1-, 2- and 3-step directions', 'Question warm-ups without a single picture', 'Groups of 5 or more: table teams in person, split screens online', 'The 5-minute questions probe']
}, {
  number: '04',
  title: 'Tell It in Order',
  weeks: 'Weeks 19–24',
  framework: 'The 5-Beat Story Map · The Linking Word Ladder',
  promise: 'Sequencing, retelling and storytelling: who, where, problem, try, end.',
  badge: 'bg-gold-deep',
  contents: ['Themes: The Camping Trip, The Beach Day, The Birthday Party, The Rainy Day, The Science Lab, The Talent Show', 'List-telling vs. storytelling: hear the difference and fix it', 'First, then, because, so, but — one rung at a time', 'WS-26 Retell Scoring Strip: score a retell while you listen', 'Peer coach pairs that double practice turns', 'The 5-minute retell probe']
}, {
  number: '05',
  title: 'Think It Through',
  weeks: 'Weeks 25–30',
  framework: 'Clue-Think-Say · The Problem-Solving Path',
  promise: 'Turn guesses into inferences and give every student a path through a problem.',
  badge: 'bg-coral-deep',
  contents: ['Themes: The Broken Robot, The Missing Cookie Mystery, The Zoo Escape, The Treasure Map, The Space Station, The Museum Night', 'Guessing vs. inferring: how to ask for the evidence', 'Name it, list options, pick one, check it', 'Inference warm-ups using only faces, sounds and objects', 'Three stations, one theme, every row covered', 'The 5-minute inference probe']
}, {
  number: '06',
  title: 'Take It Outside the Room',
  weeks: 'Weeks 31–36',
  framework: 'The Carryover Bridge · The Self-Check Scale',
  promise: 'Conversation, self-monitoring and carryover — from session to classroom to home.',
  badge: 'bg-plum-mid',
  contents: ['Themes: The Restaurant, The Sports Day, The Movie Night, The Road Trip, The Game Show, The School Fair', "I noticed, I fixed it, I didn't need help", 'WS-41 Self-Monitoring Tracker: students score their own practice', 'WS-42 Conversation Turn Tracker: turns, topics and repairs', 'Conversation circles with rotating roles', 'The 5-minute carryover snapshot']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-298" data-mp="3ux27ys2ij3yq">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-299" data-mp="3pzsaqq8vto0c">
        <div className="text-center" data-id="element-300" data-mp="3q1mwg6rz9k5h">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-301" data-mp="8rf2luwof87y">
            The complete school year
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display text-plum text-3xl sm:text-5xl" data-id="element-302" data-mp="3styvli7u6er">
            Six volumes.{' '}
            <span className="text-coral-deep" data-id="element-303" data-mp="25k71g3wpo27n">Thirty-six weeks.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-304" data-mp="l0uz1jk1qjd5">
            Each volume covers about six weeks with 6 themes and 12 sessions,
            and works on its own: placement table, sound banks, grids and
            sessions are all inside it. Start with any volume.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-305" data-mp="g9mo2j32ipxv">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-306" data-mp="2a3ssqvcv92z">
              <div className="flex items-center gap-2.5" data-id="element-307" data-mp="3oimwvekv5xj9">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base text-white ${tome.badge}`} data-id="element-308" data-mp="3ok7rt5b3bihn">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-309" data-mp="1rpl3d4oua87j">
                  Volume {tome.number} · {tome.weeks}
                </span>
              </div>
              <h3 className="mt-3 font-display text-2xl leading-snug text-plum" data-id="element-310" data-mp="1l9uo3q9u1s0f">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-gold-deep" data-id="element-311" data-mp="1gbje9fjjfhi7">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-312" data-mp="1bdrmcliw7ve0">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-313" data-mp="28ym6xr1j8y0j">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-314" data-mp="242f4w9c9c6nv">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-315" data-mp="1z7j68qciml1s" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}